import { useState } from 'react';

import { useAdminOrganizationSearchQuery } from '@/client/api/admin/organizations';
import useDebounce from '@/client/hooks/useDebounce';
import type { AdminOrganizationSearchOrg } from '@/client/types/admin/users-orgs';
import { Input } from '@/components/ui/input';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

interface OrgSearchSelectProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (org: AdminOrganizationSearchOrg) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Typeahead for `GET /api/admin/organizations/search` (§5).
 * Slim rows include state flags so callers can grey out held/deleted orgs.
 */
export default function OrgSearchSelect({
  value,
  onChange,
  onSelect,
  placeholder = 'Search orgs by name, ffid, owner…',
  className,
}: OrgSearchSelectProps) {
  const [open, setOpen] = useState(false);

  const debounced = useDebounce(value.trim(), DEBOUNCE_MS);
  const canSearch = debounced.length >= MIN_QUERY_LENGTH;

  const {
    data: orgs = [],
    isFetching,
    isError,
  } = useAdminOrganizationSearchQuery(canSearch ? debounced : null, canSearch);

  const showDropdown = open && canSearch;

  const handleSelect = (org: AdminOrganizationSearchOrg) => {
    onSelect(org);
    setOpen(false);
  };

  const rowDisabled = (org: AdminOrganizationSearchOrg) =>
    org.deleted === 1 || org.admin_hold === 1;

  return (
    <div className={`relative ${className ?? ''}`}>
      <Input
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      />

      {showDropdown && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-white shadow">
          {isFetching ? (
            <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>
          ) : isError ? (
            <div className="px-3 py-2 text-sm text-red-600">
              Failed to search organizations. Try again.
            </div>
          ) : orgs.length ? (
            orgs.map((org) => {
              const disabled = rowDisabled(org);
              return (
                <button
                  key={org.oid}
                  type="button"
                  disabled={disabled}
                  onMouseDown={(e) => e.preventDefault()}
                  className={`flex w-full flex-col items-start px-3 py-2 text-left ${
                    disabled
                      ? 'cursor-not-allowed bg-gray-50 opacity-60'
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => !disabled && handleSelect(org)}
                >
                  <span className="text-sm font-medium">{org.name}</span>
                  <span className="text-xs text-gray-500">
                    oid {org.oid}
                    {org.ffid ? ` · ${org.ffid}` : ''}
                    {org.owner ? ` · ${org.owner}` : ''}
                    {org.deleted === 1 ? ' · deleted' : ''}
                    {org.admin_hold === 1 ? ' · on hold' : ''}
                  </span>
                </button>
              );
            })
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">No organizations found</div>
          )}
        </div>
      )}
    </div>
  );
}
